/**
 * The 3D findings on their own: /report/:id/3d.
 *
 * The kiosk shows this beside the report's QR code ("View 3D report"), in a
 * frame, once the report is published. It is the same viewer as the report's
 * KQ3D drawer — posture, leg alignment, plantar pressure and foot
 * measurements, limited to the scan sections the clinic was given — filling
 * the page instead of sliding over the report.
 *
 * Public on the same footing as the report itself: holding the link is the
 * credential (firestore.rules, isPublishedReport).
 *
 * The page tells the frame around it how it is doing, so the kiosk can hold
 * its own loader until the figure is actually up:
 *
 *   window.parent.postMessage({ source: 'kinetiq-3d', status }, '*')
 *
 * status: 'loading' | 'ready' | 'failed' | 'empty' | 'missing' | 'unsupported'.
 * The message carries no patient data, so any parent may hear it.
 */

import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { limitScanToSections } from '../lib/report/reportSections';
import { drawer3dOf } from '../lib/report/drawer3d';
import { patientNameOf } from '../lib/report/scanModel';
import { K3D_CSS, KQ3DMark, hasFindings3d } from '../components/report/FindingsDrawer';
import { ReportLoader } from '../components/report/ReportLoader';
import { canRender3D } from '../lib/body3d/support';

const DrawerContent = lazy(() => import('../components/report/FindingsDrawerContent'));

export const KQ3D_MESSAGE_SOURCE = 'kinetiq-3d';

function tellParent(status) {
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ source: KQ3D_MESSAGE_SOURCE, status }, '*');
    }
  } catch { /* no parent to tell */ }
}

const MESSAGES = {
  missing: ['Report not available', 'This link may have expired, or the report may have been removed.'],
  empty: ['No 3D findings', 'This assessment has nothing to show in 3D. The full report has every reading taken.'],
  unsupported: ['3D is not available here', 'This device cannot draw the 3D view. The full report has every reading taken.'],
  failed: ['The 3D view could not be shown', 'The findings are in the full report.'],
};

export default function Report3D() {
  const { id } = useParams();
  const [scan, setScan] = useState(undefined);   // undefined while loading, null when not found
  const [scene, setScene] = useState('loading'); // the figure's own state

  useEffect(() => {
    let cancelled = false;
    tellParent('loading');
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'scans', id));
        if (!cancelled) setScan(snap.exists() ? snap.data() : null);
      } catch {
        if (!cancelled) setScan(null);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const limited = useMemo(() => (scan ? limitScanToSections(scan) : null), [scan]);
  const data = useMemo(() => (limited ? drawer3dOf(limited) : null), [limited]);

  let status = 'loading';
  if (scan === null) status = 'missing';
  else if (data && !hasFindings3d(data)) status = 'empty';
  else if (data && !canRender3D()) status = 'unsupported';
  else if (data) status = scene === 'loading' ? 'loading' : scene;

  useEffect(() => { tellParent(status); }, [status]);
  const onState = useCallback(s => setScene(s), []);

  const message = MESSAGES[status];
  return (
    <div className="k3d-page" data-testid="report-3d" data-status={status}>
      <style>{K3D_CSS}</style>
      <style>{PAGE_CSS}</style>
      <header className="k3d-head">
        <div className="k3d-head-title">
          <KQ3DMark size={30} />
          <div>
            <p className="k3d-eyebrow">3D findings</p>
            <h2>{limited ? patientNameOf(limited) : 'KinetiQ report'}</h2>
          </div>
        </div>
      </header>

      {message ? (
        <div className="k3d-page-msg">
          <b>{message[0]}</b>
          <p>{message[1]}</p>
        </div>
      ) : (
        <div className="k3d-page-body">
          {data && (
            <Suspense fallback={null}>
              <DrawerContent data={data} active onState={onState} />
            </Suspense>
          )}
          {status === 'loading' && (
            <div className="k3d-page-loader"><ReportLoader label="Loading the 3D report" /></div>
          )}
        </div>
      )}
    </div>
  );
}

const PAGE_CSS = `
html, body { margin:0; background:#f4f7f8; }
.k3d-page { position:fixed; inset:0; display:flex; flex-direction:column; background:#f4f7f8; color:#0b2434; font-family:Arial,Helvetica,sans-serif; }
.k3d-page .k3d-head { cursor:default; }
.k3d-page-body { position:relative; flex:1; min-height:0; display:flex; flex-direction:column; }
.k3d-page .k3d-stage { height:min(60vh, 700px); }
.k3d-page-loader { position:absolute; inset:0; z-index:10; display:flex; align-items:center; justify-content:center; background:#f4f7f8; }
.k3d-page-msg { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; padding:24px; }
.k3d-page-msg b { font-size:16px; margin-bottom:6px; }
.k3d-page-msg p { margin:0; color:#61737c; font-size:13px; max-width:340px; line-height:1.5; }
`;
